/**
 * 帐号管理（零知识加密保险库）三态页面：
 *   未初始化 → InitWizard；已锁定 → UnlockPanel；已解锁 → 条目列表 + 详情。
 * 服务端只存六字段密文；名称/数据解密全部在浏览器内存完成，明文不落盘、不进全局 store。
 */
import { useEffect, useMemo, useState } from 'react';
import {
  useVaultFolders,
  useVaultItems,
  useVaultStatus,
  useUpdateVaultItem,
  useDeleteVaultItem,
  useMarkVaultItemUsed,
} from '../hooks/useVault';
import { decryptItem, decryptItemName } from '../lib/vault';
import { useVault } from '../stores/vault';
import type { VaultItem, VaultItemData, VaultItemPayload, VaultItemType } from '../lib/types';
import { VAULT_TYPE_LABELS, dateTimeStr } from '../lib/utils';
import { InitWizard } from '../components/InitWizard';
import { UnlockPanel } from '../components/UnlockPanel';
import { VaultItemForm, type VaultItemEditState } from '../components/VaultItemForm';
import { Icon } from '../components/icons';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorBox,
  LoadingBlock,
  Modal,
  Pagination,
  Select,
  errMsg,
} from '../components/ui';

const TYPE_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: '全部' },
  ...(['LOGIN', 'CARD', 'NOTE', 'IDENTITY', 'WIFI'] as VaultItemType[]).map((t) => ({
    value: t,
    label: VAULT_TYPE_LABELS[t] ?? t,
  })),
];

// ==================== 详情字段渲染 ====================

function FieldRow({ label, value, secret, onCopy }: { label: string; value?: string | null; secret?: boolean; onCopy?: () => void }) {
  const [shown, setShown] = useState(false);
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
        <div className="mt-0.5 break-all font-medium text-slate-800 dark:text-slate-100">
          {secret && !shown ? '••••••••' : value}
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        {secret && (
          <button
            type="button"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700"
            onClick={() => setShown((v) => !v)}
            aria-label={shown ? '隐藏' : '显示'}
          >
            <Icon name={shown ? 'eyeOff' : 'eye'} className="h-4 w-4" />
          </button>
        )}
        {onCopy && (
          <button
            type="button"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700"
            onClick={onCopy}
            aria-label="复制"
          >
            <Icon name="copy" className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function DataFields({ data, onCopy }: { data: VaultItemData; onCopy: (text: string, label: string) => void }) {
  switch (data.type) {
    case 'LOGIN':
      return (
        <>
          <FieldRow label="用户名" value={data.username} onCopy={() => onCopy(data.username, '用户名')} />
          <FieldRow label="密码" value={data.password} secret onCopy={() => onCopy(data.password, '密码')} />
          <FieldRow label="网址" value={data.url} onCopy={data.url ? () => onCopy(data.url as string, '网址') : undefined} />
          <FieldRow label="备注" value={data.note} />
        </>
      );
    case 'CARD':
      return (
        <>
          <FieldRow label="持卡人" value={data.cardholder} />
          <FieldRow label="卡号" value={data.number} secret onCopy={() => onCopy(data.number, '卡号')} />
          <FieldRow label="有效期" value={data.expiry} />
          <FieldRow label="CVV" value={data.cvv} secret onCopy={data.cvv ? () => onCopy(data.cvv as string, 'CVV') : undefined} />
          <FieldRow label="PIN" value={data.pin} secret onCopy={data.pin ? () => onCopy(data.pin as string, 'PIN') : undefined} />
          <FieldRow label="备注" value={data.note} />
        </>
      );
    case 'NOTE':
      return <div className="whitespace-pre-wrap py-1.5 text-sm text-slate-800 dark:text-slate-100">{data.content}</div>;
    case 'IDENTITY':
      return (
        <>
          <FieldRow label="姓名" value={data.fullName} onCopy={() => onCopy(data.fullName, '姓名')} />
          <FieldRow label="证件号" value={data.idNumber} secret onCopy={() => onCopy(data.idNumber, '证件号')} />
          <FieldRow label="签发机关" value={data.issuedBy} />
          <FieldRow label="有效期至" value={data.expiresAt} />
          <FieldRow label="备注" value={data.note} />
        </>
      );
    case 'WIFI':
      return (
        <>
          <FieldRow label="网络名称" value={data.ssid} onCopy={() => onCopy(data.ssid, '网络名称')} />
          <FieldRow label="密码" value={data.password} secret onCopy={() => onCopy(data.password, '密码')} />
          <FieldRow label="加密方式" value={data.security} />
          <FieldRow label="备注" value={data.note} />
        </>
      );
    default:
      return null;
  }
}

// ==================== 详情弹窗 ====================

function ItemDetail({
  item,
  onClose,
  onEdit,
}: {
  item: VaultItem;
  onClose: () => void;
  onEdit: (edit: VaultItemEditState) => void;
}) {
  const keys = useVault((s) => s.keys);
  const [payload, setPayload] = useState<VaultItemPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const markUsed = useMarkVaultItemUsed();
  const updateItem = useUpdateVaultItem();
  const deleteItem = useDeleteVaultItem();

  useEffect(() => {
    let alive = true;
    setPayload(null);
    setError(null);
    if (!keys) return;
    decryptItem(keys, item)
      .then((p) => {
        if (alive) setPayload(p);
      })
      .catch((e: unknown) => {
        if (alive) setError(errMsg(e));
      });
    return () => {
      alive = false;
    };
  }, [keys, item]);

  async function copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  }

  return (
    <>
      <Modal open onClose={onClose} title={payload?.name ?? VAULT_TYPE_LABELS[item.type] ?? item.type}>
        <ErrorBox error={error} className="mb-3" />
        {!payload && !error && <LoadingBlock text="解密中…" />}

        {payload && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                {VAULT_TYPE_LABELS[item.type] ?? item.type}
              </Badge>
              {item.favorite && (
                <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">收藏</Badge>
              )}
              {item.archivedAt && (
                <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">已归档</Badge>
              )}
              {copied && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">已复制{copied}</span>
              )}
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              <DataFields data={payload.data} onCopy={(t, l) => void copy(t, l)} />
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
              <div>创建：{dateTimeStr(item.createdAt)}</div>
              <div>更新：{dateTimeStr(item.updatedAt)}</div>
              <div>最近使用：{dateTimeStr(item.lastUsedAt)}</div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4 dark:border-slate-700">
              <Button
                size="sm"
                variant="secondary"
                loading={markUsed.isPending}
                onClick={() => void markUsed.mutateAsync(item.id).catch(() => undefined)}
              >
                标记已使用
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  void updateItem
                    .mutateAsync({ id: item.id, favorite: !item.favorite })
                    .catch(() => undefined)
                }
              >
                {item.favorite ? '取消收藏' : '收藏'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  void updateItem
                    .mutateAsync({ id: item.id, archivedAt: item.archivedAt ? null : new Date().toISOString() })
                    .catch(() => undefined)
                }
              >
                {item.archivedAt ? '恢复' : '归档'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onEdit({ id: item.id, folderId: item.folderId, favorite: item.favorite, payload })}
              >
                编辑
              </Button>
              <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>
                删除
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="删除条目"
        message="删除后可在历史中找回最近版本，确定删除？"
        confirmText="删除"
        danger
        busy={deleteItem.isPending}
        onConfirm={() => {
          void deleteItem
            .mutateAsync(item.id)
            .then(() => {
              setConfirmDelete(false);
              onClose();
            })
            .catch(() => setConfirmDelete(false));
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}

// ==================== 已解锁视图 ====================

function VaultBrowser() {
  const keys = useVault((s) => s.keys);
  const [type, setType] = useState('');
  const [folderId, setFolderId] = useState('');
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<VaultItemEditState | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      ...(type ? { type } : {}),
      ...(folderId ? { folderId } : {}),
      archived: archived ? 'true' : 'false',
      sort: 'updatedAt',
      page,
      pageSize: 24,
    }),
    [type, folderId, archived, page],
  );
  const itemsQ = useVaultItems(query);
  const foldersQ = useVaultFolders();
  const items = useMemo(() => itemsQ.data?.items ?? [], [itemsQ.data?.items]);
  const activeItem = items.find((it) => it.id === activeId) ?? null;

  // 客户端解密名称（服务端只存密文，无法搜索/排序明文）
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    let alive = true;
    if (!keys || items.length === 0) return;
    const missing = items.filter((it) => names[it.id] === undefined);
    if (missing.length === 0) return;
    void Promise.all(
      missing.map(async (it) => [it.id, await decryptItemName(keys, it)] as const),
    )
      .then((pairs) => {
        if (alive) setNames((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [keys, items, names]);

  // 锁定后清空名称缓存（明文不留在组件状态里）
  useEffect(() => {
    if (!keys) setNames({});
  }, [keys]);

  const visible = q.trim()
    ? items.filter((it) => (names[it.id] ?? '').toLowerCase().includes(q.trim().toLowerCase()))
    : items;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-indigo-500 dark:border-slate-600 dark:bg-slate-800"
            placeholder="搜索条目名称…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Select value={folderId} onChange={(e) => { setFolderId(e.target.value); setPage(1); }} className="w-40">
          <option value="">全部文件夹</option>
          {(foldersQ.data ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </Select>
        <Button onClick={() => setFormOpen(true)}>
          <span className="flex items-center gap-1">
            <Icon name="plus" className="h-4 w-4" />
            新建条目
          </span>
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => { setType(f.value); setPage(1); }}
            className={`rounded-full px-3 py-1 text-sm transition ${
              type === f.value
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
            }`}
          >
            {f.label}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
          <input
            type="checkbox"
            checked={archived}
            onChange={(e) => { setArchived(e.target.checked); setPage(1); }}
            className="rounded border-slate-300"
          />
          显示已归档
        </label>
      </div>

      {itemsQ.isLoading && <LoadingBlock text="加载条目…" />}
      <ErrorBox error={itemsQ.error ? errMsg(itemsQ.error) : null} />

      {!itemsQ.isLoading && visible.length === 0 && (
        <EmptyState
          icon={<Icon name="vault" className="mx-auto h-10 w-10" />}
          title={q ? '没有匹配的条目' : '保险库还没有条目'}
          description={q ? '换个关键词试试' : '点击「新建条目」添加第一个加密帐号'}
          action={!q ? <Button onClick={() => setFormOpen(true)}>新建条目</Button> : undefined}
        />
      )}

      {visible.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((it) => (
            <Card
              key={it.id}
              className="cursor-pointer p-4 transition hover:shadow-md"
            >
              <button type="button" className="w-full text-left" onClick={() => setActiveId(it.id)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-medium text-slate-800 dark:text-slate-100">
                        {names[it.id] ?? '解密中…'}
                      </span>
                      {it.favorite && <Icon name="star" className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {VAULT_TYPE_LABELS[it.type] ?? it.type}
                      {it.archivedAt ? ' · 已归档' : ''}
                    </div>
                  </div>
                  <Badge className="shrink-0 bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    {VAULT_TYPE_LABELS[it.type] ?? it.type}
                  </Badge>
                </div>
              </button>
            </Card>
          ))}
        </div>
      )}

      {itemsQ.data && itemsQ.data.totalPages > 1 && (
        <Pagination page={page} totalPages={itemsQ.data.totalPages} onPage={setPage} />
      )}

      <VaultItemForm open={formOpen} onClose={() => setFormOpen(false)} />
      <VaultItemForm
        open={editing !== null}
        onClose={() => setEditing(null)}
        edit={editing}
      />
      {activeItem && (
        <ItemDetail
          item={activeItem}
          onClose={() => setActiveId(null)}
          onEdit={(e) => {
            setActiveId(null);
            setEditing(e);
          }}
        />
      )}
    </div>
  );
}

// ==================== 页面 ====================

export function VaultPage() {
  const statusQ = useVaultStatus();
  const unlocked = useVault((s) => s.unlocked);

  if (statusQ.isLoading) return <LoadingBlock text="读取保险库状态…" />;
  if (statusQ.error) {
    return <ErrorBox error={errMsg(statusQ.error)} />;
  }

  const initialized = statusQ.data?.initialized ?? false;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">帐号管理</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          零知识加密保险库：主密码仅在本机派生密钥，服务器只存密文
        </p>
      </div>

      {!initialized && <InitWizard onDone={() => void statusQ.refetch()} />}
      {initialized && !unlocked && <UnlockPanel />}
      {initialized && unlocked && <VaultBrowser />}
    </div>
  );
}
